<template>
  <v-container>
    <v-row>
      <v-col cols="12" class="d-flex align-center">
        <h1>Status</h1>
        <v-spacer />
        <v-btn
          text
          small
          color="primary"
          :disabled="isBusy"
          :loading="isBusy"
          @click="fetchStatus"
        >
          Refresh
        </v-btn>
      </v-col>
    </v-row>

    <!-- A re-fetch over data already on screen gets a thin bar rather
         than the full-page spinner, so the numbers the user is reading stay
         put instead of blanking out. -->
    <v-row v-if="isRefreshing">
      <v-col cols="12" class="py-0">
        <v-progress-linear indeterminate color="primary" />
        <span class="caption grey--text">Refreshing…</span>
      </v-col>
    </v-row>

    <v-row v-if="hasError">
      <v-col cols="12">
        <v-alert type="error" outlined prominent>
          <div class="font-weight-medium">Could not load status</div>
          <div class="body-2">{{ errorMessage }}</div>
          <v-btn
            small
            color="error"
            class="mt-2"
            :loading="isBusy"
            @click="fetchStatus"
          >
            Retry
          </v-btn>
        </v-alert>
      </v-col>
    </v-row>

    <!-- The counts initialise to 0, so rendering them before the first load
         resolves showed "0 Untagged" as though it were the answer. Hold the
         whole body back until there is something real to show. -->
    <v-row v-else-if="isInitialLoading">
      <v-col cols="12" class="text-center py-8">
        <v-progress-circular indeterminate color="primary" size="48" />
        <div class="subtitle-1 mt-4">Loading status…</div>
        <div class="caption grey--text">
          These counts are tallied across every photo in the tenant, so this
          can take up to a minute. The page is still working.
        </div>
      </v-col>
    </v-row>

    <template v-else>
      <v-row>
        <v-col cols="12">
          <v-btn-toggle v-model="statsView" mandatory dense>
            <v-btn value="total" small>Total</v-btn>
            <v-btn value="byProject" small>By project</v-btn>
          </v-btn-toggle>
        </v-col>
      </v-row>

      <v-row v-if="statsView === 'total'">
        <v-col cols="6" md="3">
          <v-card outlined class="text-center">
            <v-card-title class="headline justify-center">{{ untaggedCount }}</v-card-title>
            <v-card-subtitle>Untagged</v-card-subtitle>
          </v-card>
        </v-col>
        <v-col cols="6" md="3">
          <v-card outlined class="text-center">
            <v-card-title class="headline justify-center">{{ taggedCount }}</v-card-title>
            <v-card-subtitle>Tagged</v-card-subtitle>
          </v-card>
        </v-col>
      </v-row>

      <v-row v-else>
        <v-col cols="12" md="8">
          <v-simple-table v-if="byProject.length > 0">
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-left">Project ID</th>
                  <th class="text-right">Untagged</th>
                  <th class="text-right">Tagged</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in byProject" :key="row.project_id">
                  <td>{{ row.project_id }}</td>
                  <td class="text-right">{{ row.untagged_count }}</td>
                  <td class="text-right">{{ row.tagged_count }}</td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
          <p v-else class="text-left grey--text">No project stats available.</p>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" md="8">
          <h2 class="mb-2">Checked Out Images</h2>
          <v-simple-table v-if="checkedOutUsers.length > 0">
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-left">User</th>
                  <th class="text-left">Checked Out</th>
                  <th class="text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in checkedOutUsers" :key="row.user_id">
                  <td>{{ row.user_name }}</td>
                  <td>{{ row.checked_out_count }}</td>
                  <td>
                    <v-btn small color="warning" :disabled="isBusy" @click="doUnassign(row.user_id)">
                      Unassign
                    </v-btn>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
          <p v-else class="text-left grey--text">No users have checked out images.</p>
        </v-col>
      </v-row>
    </template>
  </v-container>
</template>

<script>
import { mapActions, mapState } from "vuex";

export default {
  name: "Status",
  data() {
    return {
      statsView: "total",
    };
  },
  computed: {
    ...mapState({
      untaggedCount: (state) => state.status.untaggedCount,
      taggedCount: (state) => state.status.taggedCount,
      checkedOutUsers: (state) => state.status.checkedOutUsers,
      byProject: (state) => state.status.byProject,
      isBusy: (state) => state.status.loading,
      hasLoaded: (state) => state.status.loaded,
      lastError: (state) => state.status.lastError,
    }),
    hasError() {
      return !!this.lastError;
    },
    /** Nothing real on screen yet — the only honest thing to show is a wait. */
    isInitialLoading() {
      return !this.hasLoaded;
    },
    /** Re-fetching over data already rendered. */
    isRefreshing() {
      return this.isBusy && this.hasLoaded;
    },
    errorMessage() {
      const err = this.lastError;
      if (!err) return "";
      // A timeout or a dropped connection rejects with no `response` at all
      // (see http.js, IT-771); "Network Error" is not something a tagger can
      // act on, so name the two cases.
      if (!err.response) {
        const isTimeout =
          err.code === "ECONNABORTED" || /timeout/i.test(err.message || "");
        return isTimeout
          ? "The server took too long to respond. This page runs a heavy query, so a retry may still time out — tell #dev-support if it keeps happening."
          : "Could not reach the server. Check your connection and try again.";
      }
      if (err.response.status === 403) {
        return "You do not have permission to view status. This page is limited to QC and admin users.";
      }
      const detail = err.response.data && err.response.data.detail;
      return detail
        ? String(detail)
        : `The server returned an error (${err.response.status}). Please try again.`;
    },
  },
  mounted() {
    this.fetchStatus();
  },
  methods: {
    ...mapActions(["fetchStatus", "unassignUser"]),
    doUnassign(userId) {
      this.unassignUser(userId);
    },
  },
};
</script>
